import get from "lodash/get";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import submit from "submit-form";
import { DEFAULT_INSIGHTS_LENS } from "../../../constants/default-insights-lens";
import i18n from "../../../i18n";
import Select from "../../../parts/inputs/select";
import TimeInput from "../../../parts/inputs/TimeInput";
import OptionToggler from "../../../parts/OptionToggler";
import SvgIcon from "../../../parts/SvgIcon";
import ToolTip from "../../../parts/tooltip";
import { getInsightsLensOptions } from "../../../utils/insights-lens";
import CollectRepMaxModal from "./CollectRepMaxModal";
import RestTimesModal from "./RestTimesModal";
import VelocityModal from "./VelocityModal";

const GeneralPreferences = ({
  userToSendInformation,
  roleName,
  activeUserUnits,
  startingCalendarDay,
  notAbleToEdit,
  activeUserRound,
  isFe,
  isCoach,
  userToBindInformation,
  hideStrengthMetrics,
  hideE1rm,
  leaderboard,
  intensityMeasure,
  defaultInsightsLens = DEFAULT_INSIGHTS_LENS,
  referAsClients,
  hideVelocitySettings,
  setHideVelocitySettings,
  thresholds,
  setThresholds,
  isOrganization,
  language,
  onFormChange,
}) => {
  const { t } = useTranslation("dashboard");
  const languageOptions = [
    { text: "English", value: "en" },
    { text: "Español", value: "es" },
  ];
  const [selectedLanguage, setSelectedLanguage] = useState(language);
  const unitsOptions = [
    { label: t("generalPreferences.metricKg"), value: "metric" },
    { label: t("generalPreferences.imperialLbs"), value: "imperial" },
  ];
  const preferredLoadInputOptions = [
    { label: t("generalPreferences.percentOf1rm"), value: "percent" },
    { label: t("generalPreferences.weight"), value: "weight" },
  ];
  const intensityMeasureOptions = [
    { label: t("generalPreferences.rpe"), value: "rpe" },
    { label: t("generalPreferences.rir"), value: "rir" },
  ];

  const defaultInsightsLensOptions = getInsightsLensOptions(t);
  const [selectedInsightsLens, setSelectedInsightsLens] = useState(
    defaultInsightsLens || DEFAULT_INSIGHTS_LENS
  );

  useEffect(() => {
    setSelectedInsightsLens(defaultInsightsLens || DEFAULT_INSIGHTS_LENS);
  }, [defaultInsightsLens]);

  const roundingOptions = [
    { label: t("generalPreferences.on"), value: true },
    { label: t("generalPreferences.off"), value: false },
  ];

  const hideStrengthMetricsOptions = [
    { label: t("generalPreferences.show"), value: false },
    { label: t("generalPreferences.hide"), value: true },
  ];
  const hideE1rmOptions = [
    { label: t("generalPreferences.show"), value: false },
    { label: t("generalPreferences.hide"), value: true },
  ];
  const startingCalendarDayOptions = [
    { text: t("generalPreferences.sunday"), value: 0 },
    { text: t("generalPreferences.monday"), value: 1 },
    { text: t("generalPreferences.tuesday"), value: 2 },
    { text: t("generalPreferences.wednesday"), value: 3 },
    { text: t("generalPreferences.thursday"), value: 4 },
    { text: t("generalPreferences.friday"), value: 5 },
    { text: t("generalPreferences.saturday"), value: 6 },
  ];
  const [startWeekDay, setStartWeekDay] = useState(startingCalendarDay);
  const defaultTimeValue = userToBindInformation?.restTime?.default;

  return (
    <form id="form-general" method="POST" data-noreset onChange={onFormChange}>
      <input
        type="hidden"
        name="userToSendInformation"
        defaultValue={userToSendInformation}
      ></input>
      <input type="hidden" name="roleName" defaultValue={roleName}></input>
      <OrganizationInfoComponent
        id={"coach-level"}
        title={t("generalPreferences.coachLevelTitle")}
        body={t("generalPreferences.coachLevelBody")}
        classnames="pb-4"
        isOrganization={isOrganization}
      />
      <div className="row first-day">
        <p className="mobile-hidden">
          {t("generalPreferences.firstDayCalendar")}
        </p>
        <div className="only-show-mobile">
          <p>{t("generalPreferences.firstDayCalendar")}</p>
        </div>
        <div className="action-buttons">
          <Select
            name={"startingCalendarDay"}
            options={startingCalendarDayOptions}
            value={startWeekDay}
            onChange={(newVal) => {
              setStartWeekDay(newVal);
              onFormChange?.();
            }}
          />
          <div className="no-tooltip mobile-hidden"></div>
        </div>
      </div>
      <div className="row">
        <p className="mobile-hidden">{t("generalPreferences.language")}</p>
        <div className="only-show-mobile">
          <p>{t("generalPreferences.language")}</p>
        </div>
        <div className="action-buttons">
          <Select
            name="language"
            options={languageOptions}
            value={selectedLanguage}
            onChange={(newVal) => {
              setSelectedLanguage(newVal);
              i18n.changeLanguage(newVal);
              onFormChange?.();
            }}
          />
          <div className="no-tooltip mobile-hidden"></div>
        </div>
      </div>
      <div className="row">
        <p className="mobile-hidden">
          {t("generalPreferences.preferredMeasurementSystem")}
        </p>
        <div className="only-show-mobile">
          <p>{t("generalPreferences.preferredMeasurementSystem")}</p>
        </div>
        <div className="action-buttons">
          <OptionToggler
            name="units"
            options={unitsOptions}
            defaultValue={activeUserUnits}
          />
          <div className="no-tooltip mobile-hidden"></div>
        </div>
      </div>
      {!!isCoach && (
        <div className="row">
          <p className="mobile-hidden">
            {t("generalPreferences.defaultInsightsLens")}
          </p>
          <div className="only-show-mobile">
            <p>{t("generalPreferences.defaultInsightsLens")}</p>
            <ToolTip
              id="default-insights-lens-tooltip"
              body={t("generalPreferences.defaultInsightsLensTooltip")}
            >
              <SvgIcon
                alt={t("generalPreferences.infoIconAlt")}
                className="cursor-pointer"
                src="/img/new-icons/question-circle.svg"
              />
            </ToolTip>
          </div>
          <div className="action-buttons">
            <Select
              name="defaultInsightsLens"
              options={defaultInsightsLensOptions}
              value={selectedInsightsLens}
              onChange={(newVal) => {
                setSelectedInsightsLens(newVal);
                onFormChange?.();
              }}
              disabled={notAbleToEdit}
            />
            <ToolTip
              className="mobile-hidden"
              id="default-insights-lens-tooltip-desktop"
              body={t("generalPreferences.defaultInsightsLensTooltip")}
            >
              <SvgIcon
                alt={t("generalPreferences.infoIconAlt")}
                className="cursor-pointer"
                src="/img/new-icons/question-circle.svg"
              />
            </ToolTip>
          </div>
        </div>
      )}
      <OrganizationInfoComponent
        id={"organization-level"}
        title={t("generalPreferences.organizationLevelTitle")}
        body={t("generalPreferences.organizationLevelBody")}
        classnames="py-5"
        isOrganization={isOrganization}
      />
      <div className="row">
        <p className="mobile-hidden">
          {t("generalPreferences.roundingToGymIncrements")}
        </p>
        <div className="only-show-mobile">
          <p>{t("generalPreferences.roundingToGymIncrements")}</p>
          <ToolTip
            id="rounding-gym-tooltip"
            body={t("generalPreferences.roundingTooltip")}
          >
            <SvgIcon
              alt={t("generalPreferences.infoIconAlt")}
              className="cursor-pointer"
              src="/img/new-icons/question-circle.svg"
            />
          </ToolTip>
        </div>
        <div className="action-buttons">
          <OptionToggler
            name="round"
            options={roundingOptions}
            defaultValue={activeUserRound}
            disabled={notAbleToEdit}
          />
          <ToolTip
            className="mobile-hidden"
            id="rounding-gym-tooltip"
            body={t("generalPreferences.roundingTooltip")}
          >
            <SvgIcon
              alt={t("generalPreferences.infoIconAlt")}
              className="cursor-pointer"
              src="/img/new-icons/question-circle.svg"
            />
          </ToolTip>
        </div>
      </div>
      {!isFe && !isCoach && (
        <div className="row">
          <p className="mobile-hidden">
            {t("generalPreferences.defaultLoadingParameter")}
          </p>
          <div className="only-show-mobile">
            <p>{t("generalPreferences.defaultLoadingParameter")}</p>
          </div>
          <div className="action-buttons">
            <OptionToggler
              name="preferredLoadInput"
              options={preferredLoadInputOptions}
              defaultValue={get(userToBindInformation, "preferredLoadInput")}
              disabled={notAbleToEdit}
            />
            <div className="no-tooltip mobile-hidden"></div>
          </div>
        </div>
      )}

      {!!(isFe || isCoach) && (
        <div className="row">
          <p className="mobile-hidden">
            {t("generalPreferences.collectRepMaxOnboarding")}
          </p>
          <div className="only-show-mobile">
            <p>{t("generalPreferences.collectRepMaxOnboarding")}</p>
          </div>
          <div className="action-buttons">
            <CollectRepMaxModal
              userToBindInformation={userToBindInformation}
              notAbleToEdit={notAbleToEdit}
            />
            <div className="no-tooltip mobile-hidden" />
          </div>
        </div>
      )}

      {!!(isFe || isCoach) && (
        <div className="row">
          <p className="mobile-hidden">
            {t("generalPreferences.strengthTrainingMetricReports")}
          </p>
          <div className="only-show-mobile">
            <p>{t("generalPreferences.strengthTrainingMetricReports")}</p>
            <ToolTip
              id="strength-training-tooltip"
              body={t("generalPreferences.strengthTrainingTooltip")}
            >
              <SvgIcon
                alt={t("generalPreferences.infoIconAlt")}
                className="cursor-pointer"
                src="/img/new-icons/question-circle.svg"
              />
            </ToolTip>
          </div>
          <div className="action-buttons">
            <OptionToggler
              name="hideStrengthMetrics"
              options={hideStrengthMetricsOptions}
              defaultValue={hideStrengthMetrics}
              disabled={notAbleToEdit}
            />
            <ToolTip
              className="mobile-hidden"
              id="strength-training-tooltip"
              body={t("generalPreferences.strengthTrainingTooltip")}
            >
              <SvgIcon
                alt={t("generalPreferences.infoIconAlt")}
                className="cursor-pointer"
                src="/img/new-icons/question-circle.svg"
              />
            </ToolTip>
          </div>
        </div>
      )}

      {!!(isFe || isCoach) && (
        <div className="row">
          <p className="mobile-hidden">
            {t("generalPreferences.estimatedOneRepMax")}
          </p>
          <div className="only-show-mobile">
            <p>{t("generalPreferences.estimatedOneRepMax")}</p>
            <ToolTip
              id="one-rep-tooltip"
              body={t("generalPreferences.oneRepTooltip")}
            >
              <SvgIcon
                alt={t("generalPreferences.infoIconAlt")}
                className="cursor-pointer"
                src="/img/new-icons/question-circle.svg"
              />
            </ToolTip>
          </div>
          <div className="action-buttons">
            <OptionToggler
              name="hideE1rm"
              options={hideE1rmOptions}
              defaultValue={hideE1rm}
              disabled={notAbleToEdit}
            />
            <ToolTip
              className="mobile-hidden"
              id="one-rep-tooltip"
              body={t("generalPreferences.oneRepTooltip")}
            >
              <SvgIcon
                alt={t("generalPreferences.infoIconAlt")}
                className="cursor-pointer"
                src="/img/new-icons/question-circle.svg"
              />
            </ToolTip>
          </div>
        </div>
      )}

      {!!(isFe || isCoach) && (
        <div className="row">
          <p className="mobile-hidden">{t("generalPreferences.leaderboard")}</p>
          <div className="only-show-mobile">
            <p>{t("generalPreferences.leaderboard")}</p>
            <ToolTip
              id="leaderboard-tooltip"
              body={t("generalPreferences.leaderboardTooltip")}
            >
              <SvgIcon
                alt={t("generalPreferences.infoIconAlt")}
                className="cursor-pointer"
                src="/img/new-icons/question-circle.svg"
              />
            </ToolTip>
          </div>
          <div className="action-buttons">
            <OptionToggler
              name="leaderboard"
              options={roundingOptions}
              defaultValue={leaderboard}
              disabled={notAbleToEdit}
            />
            <ToolTip
              className="mobile-hidden"
              id="leaderboard-tooltip"
              body={t("generalPreferences.leaderboardTooltip")}
            >
              <SvgIcon
                alt={t("generalPreferences.infoIconAlt")}
                className="cursor-pointer"
                src="/img/new-icons/question-circle.svg"
              />
            </ToolTip>
          </div>
        </div>
      )}

      <div className="row">
        <p className="mobile-hidden">
          {t("generalPreferences.intensityMeasure")}
        </p>
        <div className="only-show-mobile">
          <p>{t("generalPreferences.intensityMeasure")}</p>
          <ToolTip
            id="intensity-measure-tooltip"
            body={t("generalPreferences.intensityMeasureTooltip")}
          >
            <SvgIcon
              alt={t("generalPreferences.infoIconAlt")}
              className="cursor-pointer"
              src="/img/new-icons/question-circle.svg"
            />
          </ToolTip>
        </div>
        <div className="action-buttons">
          <OptionToggler
            name="intensityMeasure"
            options={intensityMeasureOptions}
            defaultValue={intensityMeasure}
            disabled={notAbleToEdit}
          />
          <ToolTip
            className="mobile-hidden"
            id="intensity-measure-tooltip"
            body={t("generalPreferences.intensityMeasureTooltip")}
          >
            <SvgIcon
              alt={t("generalPreferences.infoIconAlt")}
              className="cursor-pointer"
              src="/img/new-icons/question-circle.svg"
            />
          </ToolTip>
        </div>
      </div>
      {/* rest times */}
      {!!(isFe || isCoach) && (
        <div className="row">
          <p className="mobile-hidden">
            {t("generalPreferences.defaultRestTimes")}
          </p>
          <div className="only-show-mobile">
            <p>{t("generalPreferences.defaultRestTimes")}</p>
          </div>
          <div className="action-buttons">
            <TimeInput
              defaultValue={defaultTimeValue}
              name={"Default"}
              notAbleToEdit={notAbleToEdit}
            />

            <RestTimesModal
              userToBindInformation={userToBindInformation}
              notAbleToEdit={notAbleToEdit}
            />
            <div className="no-tooltip mobile-hidden"></div>
          </div>
        </div>
      )}

      {!!(isFe || isCoach) && (
        <div className="row velocity">
          <div className="velocity-title">
            <p className="mobile-hidden">
              {t("generalPreferences.velocityBetaTitle")}
            </p>
            <button
              type="button"
              data-button-type="text"
              onClick={(e) => {
                e.preventDefault();
                submit(`/actions/refresh/?`, {
                  method: "GET",
                  body: {
                    "modal[type]": "velocity-explanation",
                  },
                });
              }}
              type="button"
            >
              {t("generalPreferences.learnMore")}
            </button>
          </div>
          <div className="only-show-mobile">
            <p>{t("generalPreferences.velocityBetaTitle")}</p>
            <ToolTip
              id="hide-velocity-tooltip"
              body={t("generalPreferences.velocityTooltipHide", {
                who: referAsClients
                  ? t("generalPreferences.clientLabel")
                  : t("generalPreferences.athleteLabel"),
              })}
            >
              <SvgIcon
                alt={t("generalPreferences.infoIconAlt")}
                className="cursor-pointer"
                src="/img/new-icons/question-circle.svg"
              />
            </ToolTip>
          </div>
          <div className="action-buttons">
            <OptionToggler
              name="hideVelocitySettings"
              options={roundingOptions}
              defaultValue={!hideVelocitySettings}
              onSelectFn={(value) => {
                setHideVelocitySettings(!value);
              }}
              disabled={notAbleToEdit}
            />
            <VelocityModal
              hideVelocitySettings={hideVelocitySettings}
              thresholds={thresholds}
              setThresholds={setThresholds}
            />
            <ToolTip
              className="mobile-hidden"
              id="hide-velocity-tooltip"
              body={t("generalPreferences.velocityTooltipEnable", {
                who: referAsClients
                  ? t("generalPreferences.clientLabel")
                  : t("generalPreferences.athleteLabel"),
              })}
            >
              <SvgIcon
                alt={t("generalPreferences.infoIconAlt")}
                className="cursor-pointer"
                src="/img/new-icons/question-circle.svg"
              />
            </ToolTip>
          </div>
        </div>
      )}
    </form>
  );
};
export default GeneralPreferences;

/**
 * Component to render the organization preference information
 * @param {*} param0
 * @returns
 */
const OrganizationInfoComponent = ({
  id,
  title,
  body,
  classnames,
  isOrganization,
}) => {
  const { t } = useTranslation("dashboard");
  if (!isOrganization) {
    return null;
  }

  return (
    <div className={`flex items-center gap-2 ${classnames}`}>
      <p className="s1">{title}</p>
      <ToolTip id={id} body={body}>
        <SvgIcon
          alt={t("generalPreferences.infoIconAlt")}
          className="cursor-pointer"
          src="/img/new-icons/question-circle.svg"
        />
      </ToolTip>
    </div>
  );
};
